import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import POSLayout from "./pages/POSLayout";
import HomePage from "./pages/HomePage";
import SalesReceipt from "./pages/SalesReceipt";
import QuickPickItems from "./pages/QuickPickItems";
import AddNewItem from "./pages/AddNewItem";
import ProductList from "./pages/ProductList";
import AcceptReturn from "./pages/AcceptReturn";
import SearchProduct from "./pages/SearchProduct";
import ViewRegister from "./pages/ViewRegister";
import TodaysSales from "./pages/TodaysSales";
import OnHoldSales from "./pages/OnHoldSales";
import DailySalesReport from "./pages/DailySalesReport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("pos_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser({ username: response.data.username });
        } catch (error) {
          localStorage.removeItem("pos_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token: newToken, username: user } = response.data;
      localStorage.setItem("pos_token", newToken);
      setToken(newToken);
      setUser({ username: user });
      toast.success("Login successful!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("pos_token");
    setToken(null);
    setUser(null);
    toast.success("Logged out successfully");
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Provider
const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [discount, setDiscount] = useState(0);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        barcode: product.barcode,
        price: product.price,
        quantity: quantity,
        discount_percent: 0
      }];
    });
    toast.success(`Added ${product.name} to cart`);
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    setCartItems([]);
    setDiscount(0);
  };

  const setItemDiscount = (productId, discountPercent) => {
    setCartItems(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, discount_percent: discountPercent } : item
      )
    );
  };

  const subtotal = cartItems.reduce((sum, item) => {
    const itemDiscount = item.price * item.quantity * (item.discount_percent / 100);
    return sum + (item.price * item.quantity) - itemDiscount;
  }, 0);

  const tax = 0; // 0% tax
  const total = subtotal + tax - discount;

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      setItemDiscount,
      discount,
      setDiscount,
      subtotal,
      tax,
      total
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="loading-spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <POSLayout />
                </ProtectedRoute>
              }>
                <Route index element={<HomePage />} />
                <Route path="sales" element={<SalesReceipt />} />
                <Route path="quick-pick" element={<QuickPickItems />} />
                <Route path="add-item" element={<AddNewItem />} />
                <Route path="products" element={<ProductList />} />
                <Route path="return" element={<AcceptReturn />} />
                <Route path="search" element={<SearchProduct />} />
                <Route path="register" element={<ViewRegister />} />
                <Route path="today-sales" element={<TodaysSales />} />
                <Route path="held-sales" element={<OnHoldSales />} />
                <Route path="reports" element={<DailySalesReport />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
